import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField, MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useLoader from "../../../hooks/useLoader.js";
import useModal from "../../../hooks/useModal.js";
import { formatDateToYYYYMMDD } from "../../../Helper/functionHelper.js";
import { getPayBasisList } from "../../../services/payBasis.js";
import { generatePayrollBulk } from "../../../services/payrollPeriodic.js";

const PAY_BASIS_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" }, 
];

const employeeLabel = (emp) => (emp ? `${emp.employee_name} (${emp.employee_code})` : "");

const BulkGeneratePayroll = ({ refresh }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();

  const [payBasis, setPayBasis] = useState("DAILY");
  const isRangeCapable = payBasis === "DAILY" || payBasis === "HOURLY";
  // Payroll can't be generated for a period that hasn't happened yet -
  // caps every date field below at today, both via the picker's own
  // maxDate and (never trusting that alone) the submit-time check below.
  const today = new Date();
  const [date, setDate] = useState(new Date());
  const [rangeFrom, setRangeFrom] = useState(new Date());
  const [rangeTo, setRangeTo] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    setSelectedEmployees([]);
    setResults(null);

    const fetchEmployees = async () => {
      try {
        const response = await getPayBasisList(1, { filters: { pay_basis: payBasis }, limit: 1000 });
        setEmployees(response?.data?.data?.data || []);
      } catch (error) {
        console.error("Error fetching employees for pay basis:", error);
        toast.error("Failed to load employees for this pay basis");
      }
    };
    fetchEmployees();
  }, [payBasis]);

  const handleSelectAll = () => setSelectedEmployees(employees);

  const handleGenerate = async () => {
    if (selectedEmployees.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }
    if (isRangeCapable && rangeTo < rangeFrom) {
      toast.error('"To" date must be on or after "From" date');
      return;
    }
    if ((isRangeCapable ? rangeTo : date) > today) {
      toast.error("Cannot generate payroll for a future date");
      return;
    }

    setIsBusy(true);
    showLoader();
    try {
      const response = await generatePayrollBulk({
        employee_ids: selectedEmployees.map((e) => e.employee_id),
        pay_basis: payBasis,
        ...(isRangeCapable
          ? { period_start: formatDateToYYYYMMDD(rangeFrom), period_end: formatDateToYYYYMMDD(rangeTo) }
          : { date: formatDateToYYYYMMDD(date) }),
      });
      const data = response?.data?.data;
      setResults(data);
      toast.success(`Generated ${data?.succeeded || 0} of ${selectedEmployees.length} payroll record(s)`);
      refresh?.();
    } catch (error) {
      console.error("Error bulk generating payroll:", error);
      toast.error(error?.response?.data?.message || "Failed to generate payroll");
    } finally {
      setIsBusy(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Pay Basis
          </label>
          <TextField select size="small" fullWidth value={payBasis} onChange={(e) => setPayBasis(e.target.value)} disabled={isBusy}>
            {PAY_BASIS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {isRangeCapable ? (
          <>
            <div className="col-md-2 mb-3">
              <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
                From
              </label>
              <DatePicker
                format="dd-MMM-yyyy"
                value={rangeFrom}
                maxDate={rangeTo < today ? rangeTo : today}
                onChange={setRangeFrom}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
                To
              </label>
              <DatePicker
                format="dd-MMM-yyyy"
                value={rangeTo}
                minDate={rangeFrom}
                maxDate={today}
                onChange={setRangeTo}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </div>
          </>
        ) : (
          <div className="col-md-4 mb-3">
            <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
              Date
            </label>
            <DatePicker
              format="dd-MMM-yyyy"
              value={date}
              maxDate={today}
              onChange={setDate}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0" style={{ fontWeight: 600, fontSize: "12px" }}>
            Employees <span className="text-danger">*</span>
          </label>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll} disabled={isBusy}>
            Select All ({employees.length})
          </button>
        </div>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={employees}
          value={selectedEmployees}
          onChange={(e, value) => setSelectedEmployees(value)}
          getOptionLabel={employeeLabel}
          isOptionEqualToValue={(opt, val) => opt.employee_id === val.employee_id}
          disabled={isBusy}
          renderInput={(params) => <TextField {...params} placeholder="Select employees" />}
        />
        <div className="form-text">
          No preview or manual deductions in bulk mode - use "Generate Payroll" (single) first if you need to
          check one employee's numbers before running the whole batch.
        </div>
      </div>

      {results && (
        <div className="mb-3" style={{ maxHeight: "180px", overflowY: "auto" }}>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {results.results?.map((r) => {
                const emp = employees.find((e) => e.employee_id === r.employee_id);
                return (
                  <tr key={r.employee_id}>
                    <td>{emp ? employeeLabel(emp) : r.employee_id}</td>
                    <td className={r.success ? "text-success" : "text-danger"}>
                      {r.success ? "Generated" : r.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
        <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isBusy}>
          Close
        </button>
        <button type="button" className="btn btn-success btn-sm" disabled={isBusy} onClick={handleGenerate}>
          {isBusy ? "Generating..." : "Generate"}
        </button>
      </div>
    </div>
  );
};

export default BulkGeneratePayroll;
